import { useState, useEffect, useRef } from 'react'
import { IRevenueSpendByTeamFilters, IFilterConfig, EDatePickerMode } from '../types'
import { constructNewFilterFromConfig, constructUserFiltersFromUrl } from '../components/filters'
import * as lib from '../lib'
import { EFilterOperator, EFilterType, EFilterSpecOperator } from '../types/enum'
import { useAtomValue } from 'jotai'
import * as state from '../states'
import { useSearchParams } from 'react-router'
import dt from 'dayjs'
import * as helpers from '../helpers'
import { useTranslation } from 'react-i18next'
import { ALL_LANG_KEY } from '../types/lang'

type IUseFiltersRevenueSpendByTeamPage = {
	filter: [string, any][];
	send: boolean;
	activeFiltersCount: number;
	filterFillFactor: number;
	isFiltersPanelOpen: boolean;
	setIsFiltersPanelOpen: (value: (((prevState: boolean) => boolean) | boolean)) => void;
	revenueSpendByTeamFiltersConfig: IFilterConfig<keyof IRevenueSpendByTeamFilters>[];
	defaultFiltersValue: IRevenueSpendByTeamFilters;
	initialFiltersValue: IRevenueSpendByTeamFilters;
	clearChildFilters: number;
	setUserFilters: (value: (((prevState: (IRevenueSpendByTeamFilters | null)) => (IRevenueSpendByTeamFilters | null)) | IRevenueSpendByTeamFilters | null)) => void;
}

const [startDate, endDate] = helpers.getDateRange(EDatePickerMode.currentMonth)

const REVENUE_SPEND_BY_ACCOUNT_FILTERS_DEFAULT: IRevenueSpendByTeamFilters = {
	teamReport: '',
	at: [startDate ? startDate.format('YYYY-MM-DD') : '', endDate ? endDate.format('YYYY-MM-DD') : '']
	//at: [dt().startOf('month').format('YYYY-MM-DD'), dt().startOf('day').format('YYYY-MM-DD')]
}

export const useRevenueSpendByTeamFiltersPage = (team: string): IUseFiltersRevenueSpendByTeamPage => {
	const { t } = useTranslation()
	const isFirstRender = useRef<boolean>(true)
	const [teamPrev, setTeamPrev] = useState<string>(team)

	const profile = useAtomValue(state.profileAtom)
	const [searchParams, setSearchParams] = useSearchParams()

	const urlFilter = (() => {
		try {
			return lib.filterSearchParams(searchParams.get('f')) ?? []
		} catch (error) {
			console.error(error)
			return []
		}
	})()

	const [filter, setFilter] = useState<[string, any][]>([...urlFilter]) // main filter with mixins
	const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState<boolean>(false)
	const [activeFiltersCount, setActiveFiltersCount] = useState<number>(0)

	const [clearChildFilters, setClearChildFilters] = useState<number>(0)
	const [send, setSend] = useState<boolean>(false)
	const [userFilters, setUserFilters] = useState<IRevenueSpendByTeamFilters | null>(null)

	const filterFillFactor = !!userFilters ? (100 * activeFiltersCount / Object.keys(userFilters).length) : 0

	const revenueSpendByTeamFiltersConfig: IFilterConfig<keyof IRevenueSpendByTeamFilters>[] = [
		/*{
			name: 'teamReport',
			label: 'Team Report',
			type: EFilterType.input,
			operator: EFilterOperator.isLike,
			specOperator: EFilterSpecOperator.everywhere,
			styles: { width: '200px' }
		},*/
		{
			name: 'at',
			label: t(ALL_LANG_KEY.revenue_spend_by_account_list_table.column_date),
			type: EFilterType.dateRange,
			operator: EFilterOperator.isBetween,
			styles: { width: '280px' },
			isClearable: false
		}
	]

	const initialUserFilters = constructUserFiltersFromUrl(
		urlFilter,
		revenueSpendByTeamFiltersConfig,
		REVENUE_SPEND_BY_ACCOUNT_FILTERS_DEFAULT
	)

	useEffect(() => {
		let _userFilters = userFilters
		//console.log('hook.useRevenueSpendByTeamFiltersPage useEffect::START:: ', { send, team, teamPrev, userFilters, _userFilters, filter })
		if (send && teamPrev !== team) {
			// reset filters [RFC] onTeam (menu tabs) changed
			setTeamPrev(team)
			//console.log('!!! clear main filter:: send && teamPrev !== team !!!')
			setFilter([]) // clear main filter with mixins
			_userFilters = REVENUE_SPEND_BY_ACCOUNT_FILTERS_DEFAULT
			setUserFilters(REVENUE_SPEND_BY_ACCOUNT_FILTERS_DEFAULT)
			setClearChildFilters(prev => prev + 1) // clearAllChildFilters command-flag
		}

		if (!!_userFilters) {
			setSend(true)
			//console.log('hook.useFiltersOfferScopePage useEffect::MIDDLE:: ', { userFilters, _userFilters, filter })
			const { newFilter, activeFiltersCnt } = constructNewFilterFromConfig(
				_userFilters,
				revenueSpendByTeamFiltersConfig
			)
			const newFilterStr = lib.filterSetup([...newFilter ?? []])

			setSearchParams((prev) => {
				if (newFilterStr) {
					prev.set('f', newFilterStr)
				} else {
					prev.delete('f')
				}

				// after first pageLoad/pageRefresh resetting is not needed
				if (!isFirstRender.current) {
					prev.delete('page')
				} else {
					isFirstRender.current = false
				}

				if (!!team && `${team}` !== prev.get('team')) {
					prev.set('team', encodeURIComponent(team))
				}

				return prev
			})

			if (team) {
				newFilter.push([`teamReport${EFilterOperator.isEqual}`, team])
			}

			setFilter(newFilter)
			setActiveFiltersCount(activeFiltersCnt)

			// to open FiltersPanel if there were previously active filters on the page [onRefreshPage case]
			if (activeFiltersCnt > 0) {
				setIsFiltersPanelOpen(true)
			}
			console.log('hook.useRevenueSpendByTeamFiltersPage:: useEffect::END:: ', {
				userFilters,
				_userFilters,
				filter,
				newFilter,
				newFilterStr
			})
		}
	}, [userFilters, team])

	return {
		filter,
		send,
		activeFiltersCount,
		filterFillFactor,
		isFiltersPanelOpen,
		setIsFiltersPanelOpen,
		revenueSpendByTeamFiltersConfig,
		defaultFiltersValue: REVENUE_SPEND_BY_ACCOUNT_FILTERS_DEFAULT,
		initialFiltersValue: initialUserFilters,
		clearChildFilters,
		setUserFilters
	}
}